/* Equipping and unequipping weapons, by the Equipment rules: up to two weapons wait in the sheet's Inventory
   Weapon areas, and an active weapon swaps with one of them (free during a rest or moment of calm, otherwise
   a Stress, which the player marks). Each move is the change to make, or the reason it cannot be made. */

import { BRAWLERS_STRIKE, Catalog } from './catalog';
import type { Character } from './model';

export type GearMove = { change: Partial<Character> } | { reason: string };

/** The sheet has two Inventory Weapon areas. */
export const INVENTORY_WEAPONS = 2;

/** The rules' line on swapping, shown beside the buttons. */
export const SWAP_RULE = 'You can swap an Inventory Weapon with an Active Weapon at no cost during a rest or moment of calm; otherwise, you must mark a Stress to do so.';

/** The rules' line on armor, shown beside its buttons. */
export const ARMOR_RULE = "You can't equip armor while in danger or under pressure; otherwise, you can equip or unequip it without cost. Armor you take off keeps its marked Armor Slots, and armor can't be carried in your inventory.";

/** Puts an active weapon away in the inventory. */
export function unequipWeapon(c: Character, slot: 'primary' | 'secondary'): GearMove {
  const name = c[slot];
  if (!name) return { reason: `There is no ${slot} weapon to put away.` };
  if (name === BRAWLERS_STRIKE.name) return { reason: "Brawler's Strike is your bare hands; there is nothing to put away." };
  if (c.inventoryWeapons.length >= INVENTORY_WEAPONS) return { reason: 'Both Inventory Weapon slots are full.' };
  return { change: { [slot]: '', inventoryWeapons: [...c.inventoryWeapons, name] } };
}

/** Takes an inventory weapon in hand: into its own slot, the weapon there going to the inventory, and a two-handed weapon freeing the other hand too. */
export function equipWeapon(c: Character, catalog: Catalog, name: string): GearMove {
  const weapon = catalog.weapon(name);
  if (!weapon || !c.inventoryWeapons.includes(name)) return { reason: `${name} is not in the inventory.` };
  const slot = weapon.category === 'Secondary' ? 'secondary' : 'primary';
  const primary = catalog.weapon(c.primary);
  if (slot === 'secondary' && primary?.burden === 'Two-Handed') return { reason: `${primary.name} takes both hands.` };
  const freed = slot === 'primary' && weapon.burden === 'Two-Handed' && c.secondary ? [c.secondary] : [];
  const inventory = [...c.inventoryWeapons.filter((n) => n !== name), ...(c[slot] ? [c[slot]] : []), ...freed];
  if (inventory.length > INVENTORY_WEAPONS) return { reason: `There is no room in the inventory for the weapons ${name} replaces.` };
  return { change: { [slot]: name, ...(freed.length ? { secondary: '' } : {}), inventoryWeapons: inventory } };
}
